// eslint-disable-next-line eag/no-external-date-time-format-functions
import {addHours, format, parse} from 'date-fns';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  openConfirmDialog,
} from 'platform/components';
import {Text, Box, HStack, Space, Stack, VStack, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {TaskPriorityEnum, TaskTypeEnum} from '@dms/api/core';
import {
  ListTeamsResponseBody,
  useGetCurrentUserInfoQuery,
  useGetUsersQuery,
  useListTeamsQuery,
} from '@dms/api/customer';
import {
  EntityResourceIds,
  TaskPriorityEnumSchema,
  TaskStateEnumSchema,
  TaskTypeEnumSchema,
  UserStateFilterSchema,
} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  AssignmentOption,
  CommentsWidget,
  DeletedUserAlertField,
  isTeamValue,
  mapUsersToSelectOptions,
  moveToHeadById,
  openPredefinedNotesDropdown,
  toTeamValue,
  useUserAbsenceIndicators,
  useUsersWithPersistedAssignee,
} from '@dms/shared';

import {parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {TASK_PRIORITY_LABEL_MAP, TASK_TYPE_LABEL_MAP} from '../../constants/task';
import type {TaskForm as TaskFormType} from '../../types/TaskForm';
import {TaskFormInput} from '../../types/TaskFormInput';
import {TaskRelationProps} from '../../types/TaskRelationProps';
import {TaskRelatesCard} from '../TaskRelatesCard/TaskRelatesCard';
import {TaskStateSwitcherField} from '../TaskStateSwitcherField/TaskStateSwitcherField';
import {buildAssignedToOptions} from './utils/buildAssignedToOptions';
import {getTaskFormDefaultValues} from './utils/getTaskFormDefaultValues';
import {zodTaskFormSchema} from './zodTaskFormSchema';

const buildTeamOptions = (teams: ListTeamsResponseBody[] | undefined): AssignmentOption[] =>
  teams?.map((team) => ({value: toTeamValue(team.id), label: team.name})) ?? [];

interface TaskFormProps extends RequiredTestIdProps, TaskRelationProps {
  onSubmit: FormSubmitHandler<TaskFormType>;
  values?: TaskFormInput;
  onDelete?: () => Promise<void>;
  /** Opens the saved task's history; the link is drawn only when given. */
  onShowHistory?: () => void;
  /** While a save or delete is in flight: a dialog opened on top would be the one it closes. */
  isHistoryDisabled?: boolean;
  isCreateTask: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
}

// eslint-disable-next-line max-lines-per-function -- T20-136397: grew with the task form; split planned
export function TaskForm(props: TaskFormProps) {
  const dateFormatter = useDateTimeFormatter();
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {data: users, isLoading: areUsersLoading} = useGetUsersQuery({
    stateFilter: UserStateFilterSchema.enum.active,
  });
  const {getStatusIndicator} = useUserAbsenceIndicators();

  const {data: assigneeTeams, isLoading: areTeamsLoading} = useListTeamsQuery({active: true});

  const assignedTo = props.values?.assignedTo;
  const assignedToUserId =
    isNotNil(assignedTo) && !isTeamValue(assignedTo) ? assignedTo : undefined;
  // Two independent lists so a deleted assignee is never offered as a reporter and
  // vice versa: each picker gets the active users plus only its own persisted user.
  const usersWithReportedBy = useUsersWithPersistedAssignee(users, props.values?.reportedBy);
  const usersWithAssignee = useUsersWithPersistedAssignee(users, assignedToUserId);

  const reportedByOptions = mapUsersToSelectOptions(
    moveToHeadById(currentUser?.id ?? '', usersWithReportedBy),
    getStatusIndicator
  );
  const assigneeUserOptions = mapUsersToSelectOptions(
    moveToHeadById(currentUser?.id ?? '', usersWithAssignee),
    getStatusIndicator
  );

  const assignedToOptions = buildAssignedToOptions(
    assigneeUserOptions,
    buildTeamOptions(assigneeTeams)
  );
  const isProcessing = props.isLoading || areUsersLoading || areTeamsLoading;

  const handleDelete = () => {
    if (props.onDelete) {
      openConfirmDialog({
        onConfirm: () => {
          props.onDelete?.();
        },
      });
    }
  };

  return (
    <Form<TaskFormType>
      data-testid={suffixTestId('taskForm', props)}
      onSubmit={props.onSubmit}
      experimentalZodSchema={zodTaskFormSchema}
      defaultValues={getTaskFormDefaultValues(dateFormatter, currentUser, {
        ...props.values,
        relatedRecordId:
          props.relatedRecordId && props.resourceId
            ? {
                id: props.relatedRecordId,
                resourceId: props.resourceId as string,
              }
            : null,
      })}
    >
      {(control, formApi) => (
        <>
          <Stack direction={['column', 'column', 'row']} spacing={4}>
            <VStack grow={2} spacing={4} maxWidth={['100%', '100%', '80%']}>
              <FormField
                data-testid={suffixTestId('title', props)}
                control={control}
                name="title"
                type="text"
                label={i18n.t('entity.task.title')}
                isRequired
                isDisabled={props.isReadOnly}
                labelActions={
                  props.isReadOnly
                    ? undefined
                    : [
                        {
                          title: i18n.t('general.labels.templates'),
                          leftIcon: 'content/add_circle',
                          onClick: (event) => {
                            openPredefinedNotesDropdown(event, {
                              note: formApi.watch('title') ?? null,
                              resource: EntityResourceIds.task,
                              context: 'title',
                              onPrefill: (text) => {
                                formApi.setValue('title', text);
                              },
                              'data-testid': suffixTestId('titleTemplates', props),
                            });
                          },
                        },
                      ]
                }
              />
              <FormField
                data-testid={suffixTestId('type', props)}
                control={control}
                name="type"
                type="chips"
                isDisabled={props.isReadOnly}
                label={i18n.t('entity.task.type')}
                isRequired
                options={TaskTypeEnumSchema.options
                  .filter((option) => !props.isReadOnly || option === formApi.getValues().type)
                  .map((option) => ({
                    value: option,
                    label: i18n.t(TASK_TYPE_LABEL_MAP[option as TaskTypeEnum]),
                  }))}
              />
              <HStack spacing={4} align="center">
                <Box width="100%">
                  <FormField
                    data-testid={suffixTestId('dueDate', props)}
                    control={control}
                    isDisabled={props.isReadOnly}
                    name="dueDate"
                    type="date"
                    label={i18n.t('entity.task.dueDate')}
                    isRequired={
                      isNotNilOrEmpty(formApi.getValues().dueTo) ||
                      isNotNilOrEmpty(formApi.getValues().dueFrom)
                    }
                  />
                </Box>
                <Box width="100%">
                  <FormField
                    data-testid={suffixTestId('dueFrom', props)}
                    control={control}
                    isDisabled={props.isReadOnly}
                    name="dueFrom"
                    type="time"
                    label={i18n.t('entity.task.dueFrom')}
                    onChange={(dueFrom) => {
                      if (isNotNil(dueFrom)) {
                        const dueFromDate = parse(dueFrom, 'HH:mm', new Date());
                        const dueTo = formApi.watch('dueTo');

                        if (isNil(dueTo) || dueTo === '00:00') {
                          formApi.setValue('dueTo', format(addHours(dueFromDate, 1), 'HH:mm'));
                        }
                      }
                    }}
                  />
                </Box>
                <Box width="100%">
                  <FormField
                    data-testid={suffixTestId('dueTo', props)}
                    control={control}
                    isDisabled={props.isReadOnly}
                    name="dueTo"
                    type="time"
                    isRequired={isNotNilOrEmpty(formApi.getValues().dueFrom)}
                    label={i18n.t('entity.task.dueTo')}
                  />
                </Box>
              </HStack>
              <FormField
                data-testid={suffixTestId('description', props)}
                control={control}
                isDisabled={props.isReadOnly}
                name="description"
                type="textarea"
                isScrollable
                minRows={1}
                maxRows={10}
                label={i18n.t('entity.task.description')}
                labelActions={
                  props.isReadOnly
                    ? undefined
                    : [
                        {
                          title: i18n.t('general.labels.templates'),
                          leftIcon: 'content/add_circle',
                          onClick: (event) => {
                            openPredefinedNotesDropdown(event, {
                              note: formApi.watch('description') ?? null,
                              resource: EntityResourceIds.task,
                              context: 'description',
                              onPrefill: (text) => {
                                formApi.setValue('description', text);
                              },
                              'data-testid': suffixTestId('descriptionTemplates', props),
                            });
                          },
                        },
                      ]
                }
              />

              <Show when={isNotNilOrEmpty(formApi.getValues()?.relatedRecordId)}>
                <div>
                  <Label>{i18n.t('entity.task.labels.relatesTo')}</Label>
                  {(() => {
                    formApi.watch('relatedRecordId');
                    return (
                      <TaskRelatesCard
                        data-testid={suffixTestId('relatesTo', props)}
                        relatedRecordId={props.relatedRecordId ?? ''}
                        resourceId={props.resourceId!}
                        isReadOnly={props.isReadOnly}
                        onRelationRemove={() => {
                          formApi.setValue('relatedRecordId', null);
                        }}
                      />
                    );
                  })()}
                </div>
              </Show>

              <Show when={props.values?.id}>
                <CommentsWidget
                  title={i18n.t('page.comments.labels.comments')}
                  resourceId={EntityResourceIds.task}
                  recordId={props.values?.id ?? ''}
                  isAddDisabled={props.isReadOnly}
                  isDeleteDisabled={props.isReadOnly}
                />
              </Show>
            </VStack>
            <Box
              flexGrow={2}
              width={['100%', '100%', '20%']}
              backgroundColor="palettes.neutral.20.100"
              borderRadius="medium"
              padding={4}
            >
              <VStack spacing={4}>
                <HStack>
                  <TaskStateSwitcherField
                    data-testid={suffixTestId('state', props)}
                    control={control}
                    isDisabled={props.isReadOnly}
                    name="state"
                  />
                </HStack>
                <VStack spacing={1}>
                  <FormField
                    isLoading={isProcessing}
                    isDisabled={isProcessing || props.isReadOnly}
                    data-testid={suffixTestId('assignedTo', props)}
                    control={control}
                    name="assignedTo"
                    isRequired
                    type="choice"
                    options={assignedToOptions}
                    label={i18n.t('entity.task.assignedTo')}
                  />
                  <DeletedUserAlertField
                    control={control}
                    name="assignedTo"
                    users={usersWithAssignee}
                    data-testid={suffixTestId('assignedTo', props)}
                  />
                </VStack>
                <VStack spacing={1}>
                  <FormField
                    isLoading={isProcessing}
                    isDisabled={isProcessing || props.isReadOnly}
                    data-testid={suffixTestId('reportedBy', props)}
                    control={control}
                    name="reportedBy"
                    type="choice"
                    isRequired
                    options={reportedByOptions}
                    label={i18n.t('entity.task.reportedBy')}
                  />
                  <DeletedUserAlertField
                    control={control}
                    name="reportedBy"
                    users={usersWithReportedBy}
                    data-testid={suffixTestId('reportedBy', props)}
                  />
                </VStack>
                <FormField
                  data-testid={suffixTestId('priority', props)}
                  control={control}
                  name="priority"
                  type="chips"
                  isDisabled={props.isReadOnly}
                  label={i18n.t('entity.task.priority')}
                  options={TaskPriorityEnumSchema.options
                    .filter(
                      (option) => !props.isReadOnly || option === formApi.getValues().priority
                    )
                    .map((option) => ({
                      value: option,
                      label: i18n.t(TASK_PRIORITY_LABEL_MAP[option as TaskPriorityEnum]),
                    }))}
                />
                <HStack justify="space-between">
                  <Show when={isNotNilOrEmpty(props.values?.createdAt)}>
                    <div>
                      <Label>{i18n.t('general.labels.createdAt')}</Label>
                      <Text size="small">
                        {props.values?.createdAt
                          ? dateFormatter('dateTimeShort', parseDate(props.values?.createdAt))
                          : null}
                      </Text>
                    </div>
                  </Show>
                  <Show
                    when={
                      props.values?.state === TaskStateEnumSchema.enum.DONE &&
                      isNotNilOrEmpty(props.values?.finishedAt)
                    }
                  >
                    <div>
                      <Label>{i18n.t('entity.task.finishedAt')}</Label>
                      <Text size="small">
                        {props.values?.finishedAt
                          ? dateFormatter('dateTimeShort', parseDate(props.values?.finishedAt))
                          : null}
                      </Text>
                    </div>
                  </Show>
                </HStack>
                <Show when={isNotNil(props.onShowHistory) && isNotNilOrEmpty(props.values?.id)}>
                  <div>
                    <Button
                      variant="link"
                      leftIcon="action/history"
                      onClick={props.onShowHistory}
                      isDisabled={props.isHistoryDisabled}
                      title={i18n.t('general.labels.history')}
                      data-testid={suffixTestId('history', props)}
                    />
                  </div>
                </Show>
                <Show when={isNotNil(props.onDelete) && isNotNilOrEmpty(props.values?.id)}>
                  <div>
                    <Button
                      variant="dangerLink"
                      leftIcon="action/delete"
                      onClick={handleDelete}
                      isLoading={props.isLoading}
                      isDisabled={props.isLoading}
                      title={i18n.t('entity.task.actions.deleteTask')}
                    />
                  </div>
                </Show>
              </VStack>
            </Box>
          </Stack>
          <Space vertical={4} />
          <DialogFooter data-testid={props['data-testid']}>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={closeCurrentDialog}
                title={i18n.t('general.actions.discard')}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                variant="primary"
                isLoading={isProcessing}
                type="submit"
                control={control}
                title={
                  props.isCreateTask
                    ? i18n.t('general.actions.create')
                    : i18n.t('general.actions.save')
                }
                isDisabled={isProcessing || props.isReadOnly}
                data-testid={suffixTestId('submit', props)}
              />
            </ButtonGroup>
          </DialogFooter>
        </>
      )}
    </Form>
  );
}
